<template>
  <div class="container-page">
    <p class="title-inner">Здравоохранение</p>
    <div class="mb-16">
      <ButtonComponent
        v-permission="permissions.create"
        class="mr-2"
        mod="gradient-bg"
        buttonType="button"
        :disabled="importInProgress"
        @click="editCard(0)"
      >
        Создать
      </ButtonComponent>
      <ButtonComponent
        v-permission="permissions.import"
        mod="gradient-bg"
        buttonType="button"
        :disabled="importInProgress"
        @click="importExcel()"
      >
        {{ !importInProgress ? 'Загрузить из Excel' : 'Загрузка...' }}
      </ButtonComponent>
    </div>
    <div class="mb-4">
      <DefaultFilter :filterValue="filter" @loadFilter="onLoadFilter" />
    </div>
    <div v-if="isAccessAllowed">
      <div class="table-container mb-16 js-table-arrow">
        <table class="stockTable">
          <thead>
            <tr>
              <th>Год</th>
              <th>Месяц</th>
              <th>Страна</th>
              <th>Новые случаи заражения COVID-19</th>
              <th>Количество вакцинированных</th>
              <th></th>
              <th></th>
            </tr>
            <tr>
              <th class="filter">
                <FilterInput
                  placeholder="Год"
                  v-model="filter.year"
                  type="number"
                  v-on:valueChangedDebounced="changeFilter"
                />
              </th>
              <th class="filter">
                <FilterInput
                  :select2Options="months"
                  placeholder="Выберите"
                  type="select2"
                  v-model="filter.month"
                  :select2Settings="select2Settings"
                  v-on:valueChangedDebounced="changeFilter"
                />
              </th>
              <th class="filter">
                <FilterInput
                  :select2Options="countries"
                  placeholder="Выберите"
                  type="select2"
                  v-model="filter.countryId"
                  :select2Settings="select2Settings"
                  v-on:valueChangedDebounced="changeFilter"
                />
              </th>
              <th class="filter">
                <FilterInput
                  placeholder="От"
                  v-model="filter.covidNewCaseCountFrom"
                  type="number"
                  v-on:valueChangedDebounced="changeFilter"
                />
                <FilterInput
                  placeholder="До"
                  v-model="filter.covidNewCaseCountTo"
                  type="number"
                  v-on:valueChangedDebounced="changeFilter"
                />
              </th>
              <th class="filter">
                <FilterInput
                  placeholder="От"
                  v-model="filter.vaccinatedCountFrom"
                  type="number"
                  v-on:valueChangedDebounced="changeFilter"
                />
                <FilterInput
                  placeholder="До"
                  v-model="filter.vaccinatedCountTo"
                  type="number"
                  v-on:valueChangedDebounced="changeFilter"
                />
              </th>
              <th class="filter"></th>
              <th class="filter"></th>
            </tr>
          </thead>
          <tbody v-if="page.items?.length && !isLoading">
            <tr v-for="item in page.items" :key="item.id">
              <td>{{ item.year }}</td>
              <td>{{ item.monthName }}</td>
              <td>{{ item.country?.name }}</td>
              <td>{{ item.covidNewCaseCount }}</td>
              <td>{{ item.vaccinatedCount }}</td>
              <td>
                <IconComponent
                  v-permission="permissions.edit"
                  :disabled="importInProgress"
                  @click="editCard(item.id)"
                  name="mode"
                />
              </td>
              <td>
                <IconComponent
                  v-permission="permissions.delete"
                  :disabled="importInProgress"
                  @click="deleteCard(item.id)"
                  name="delete"
                />
              </td>
            </tr>
          </tbody>
          <tbody v-else-if="isLoading">
            <tr>
              <td class="text-center" colspan="7">Загрузка...</td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td class="text-center" colspan="7">Нет данных.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="page.pagesTotal > 1">
        <Pagination
          :pageCount="page.pagesTotal"
          :currentPage="page.pageNumber"
          @change="onPageChange($event, false)"
          @more="onPageChange($event, true)"
        />
      </div>
    </div>
    <section>
      <ModalComponent v-model="importModal" title="Импорт файла" @close="closeModal">
        <a href="javascript:" @click="getTemplate">Скачать шаблон</a>
        <ImportModal @doimport="doImport" @close="closeModal" />
      </ModalComponent>
    </section>
  </div>
</template>

<script>
  import DefaultFilter from '@/common/components/DefaultFilter.vue';
  import FilterInput from '@/common/components/FilterInput.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import { loadFilter, saveFilter } from '@/common/utils/filterManager';

  import ImportModal from '@/components/modals/ImportModal.vue';

  import countryApi from '@/modules/nsi/api/country';

  import IconComponent from '../../../common/ui/IconComponent.vue';
  import ModalComponent from '../../../components/modals/ModalComponent.vue';
  import Api from '../api/healthcare';

  const PERMISSIONS = {
    view: Constants.Permissions.HealthcareView,
    import: Constants.Permissions.HealthcareImport,
    create: Constants.Permissions.HealthcareCreate,
    edit: Constants.Permissions.HealthcareEdit,
    delete: Constants.Permissions.HealthcareDelete,
  };

  export default {
    name: 'HealthcareStats',
    components: { ImportModal, ModalComponent, ButtonComponent, FilterInput, DefaultFilter, IconComponent },
    mixins: [security],
    data() {
      return {
        page: {},
        request: {
          pageNumber: 1,
          pageSize: Constants.pageSize,
        },
        importInProgress: false,
        importModal: false,
        isLoading: false,

        select2Settings: Constants.select2Settings,
        countries: [],
        filter: {},
        months: Constants.monthList,
      };
    },
    computed: {
      permissions: () => PERMISSIONS,
    },
    created() {
      this.checkPermissionAndRedirect(PERMISSIONS.view).then((isAllowed) => {
        if (!isAllowed) return;

        loadFilter(this.$options.name).then((response) => {
          let filter = response;
          if (filter) {
            this.filter = filter;
          }
          this.loadDictionaries();
          this.loadPage();
        });
      });
    },
    methods: {
      loadDictionaries() {
        countryApi.search().then((response) => {
          this.countries = response.data.items.map((x) => ({ id: x.id, text: x.name }));
        });
      },
      loadPage(append) {
        this.isLoading = !append;
        var params = Object.assign(this.filter, this.request);
        Api.search(params)
          .then((response) => {
            if (!append) {
              this.page = {};
              this.$nextTick(() => {
                this.page = response.data;
              });
            } else if (response.data) {
              this.page.pageNumber = response.data.pageNumber;
              this.page.items = this.page.items.concat(response.data.items);
            }
            this.isLoading = false;
          })
          .catch(() => {
            this.isLoading = false;
          });
      },
      importExcel() {
        this.importModal = true;
      },
      doImport(file) {
        this.closeModal();
        this.importInProgress = true;
        Api.uploadFile(file)
          .then(() => {
            Constants.alert.fire('Импорт', 'Импорт успешно выполнен.', 'success');
            this.loadPage();
            this.importInProgress = false;
          })
          .catch((error) => {
            console.log(error?.response?.data);
            Constants.alert.fire('Импорт', 'Ошибка при импорте данных.', 'error');
            this.importInProgress = false;
          });
      },
      onPageChange(pageNumber, addToExist) {
        this.request.pageNumber = pageNumber;
        this.loadPage(addToExist);
      },
      closeModal() {
        this.importModal = false;
      },
      changeFilter() {
        this.request.pageNumber = 1;
        saveFilter(this.$options.name, this.filter);
        this.loadPage(false);
      },
      onLoadFilter(item) {
        this.filter = item;
        this.changeFilter();
      },
      editCard(id) {
        this.$router.push({ name: 'HealthcareStatsCard', params: { id: id, action: 'edit' } });
      },
      deleteCard(id) {
        Constants.alert.fire(Constants.confirmOptions.delete).then((res) => {
          if (res.isConfirmed) {
            Api.delete(id)
              .then((response) => {
                console.log(response);
                Constants.alert.fire('Удаление', 'Запись удалена', 'success');
                this.loadPage();
              })
              .catch((error) => {
                Constants.alert.fire(
                  'Удаление',
                  error.response && error.response.data ? error.response.data : 'Запись не удалена',
                  'error',
                );
              });
          }
        });
      },
      getTemplate() {
        Api.getTemplate();
      },
    },
  };
</script>
